/**
 * Spotlights section
 * Dedicated copy of the Campaign cards section's interaction script,
 * for the Spotlights section only. Single-select pill filtering +
 * "Load more" pagination. "All" can show cards in a fresh random
 * order on every page load, while every other filter keeps the
 * merchant's configured sort order. Supports multiple instances of
 * the section on one page.
 *
 * Beyond 250 entries: shop.metaobjects[type].values maxes out at 250
 * per Liquid {% paginate %} page (Shopify's own hard ceiling — see the
 * comment at the top of sections/spotlights.liquid). Once there are
 * more than 250 entries, this script fetches the remaining Shopify
 * pages (2, 3, ...) in the background right after the visible page
 * loads, using the Section Rendering API
 * (?section_id=<id>&page=<n>), pulls the [data-card] elements out of
 * each response, and appends them into the grid. The visible "Load
 * more" button only ever reveals cards already sitting in the DOM, so
 * it keeps working exactly the same regardless of how many entries
 * exist in total — it just has more to reveal once background loading
 * catches up.
 *
 * Confirmed on this store: requesting ?page=2 through the Section
 * Rendering API for this section does NOT reliably advance the
 * metaobjects {% paginate %} loop — the response can come back as page
 * 1 again, which is what was doubling every entry (500 cards in the
 * DOM for 250 real ones). Each response's own root element reports
 * which page it actually rendered (data-current-page); before
 * appending anything, this script checks that against the page number
 * it asked for and bails out if they don't match, rather than trust
 * the request advanced. Every card also carries a data-handle
 * attribute (its metaobject's unique, system-assigned handle) as a
 * second safety net, deduped the same way.
 *
 * Known trade-offs of this approach (documented rather than silently
 * accepted):
 * - While background pages are still loading, "All" shows cards in
 *   plain server order rather than shuffled; the one-time shuffle
 *   runs once every page has arrived. On a normal connection this
 *   window is a fraction of a second per extra 250 entries.
 * - A campaign-type filter pill only renders if that type appears
 *   somewhere in the FIRST 250 entries (pills are decided once, from
 *   the initial server render). With only 8 possible campaign types
 *   in total, this is not a realistic problem in practice, but is
 *   worth knowing if entries are ever restructured drastically.
 * - If a fetch fails (e.g. the visitor loses their connection
 *   mid-load), background loading just stops quietly — whatever
 *   already loaded stays fully usable, and a page refresh retries
 *   from the beginning.
 * - Because ?page= isn't reliably reaching this loop, entries beyond
 *   the first 250 may not load in the background at all right now (the
 *   page-number check above stops the very first background request
 *   rather than let it duplicate page 1). That's a visibility gap, not
 *   a duplication bug — see the note in sections/spotlights.liquid for
 *   the real fix once entries meaningfully exceed 250.
 */
(function () {
  'use strict';

  function initCampaignCards(root) {
    if (!root || root.dataset.campaignCardsInitialized) return;
    root.dataset.campaignCardsInitialized = 'true';

    var pageSize = parseInt(root.dataset.pageSize, 10) || 12;
    var randomizeAll = root.dataset.randomizeAll === 'true';
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-filter-pill]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-card]'));

    // Every handle already rendered (from the initial page, then topped
    // up as background pages come in) — the source of truth that keeps
    // fetchNextPage() from ever adding the same entry twice.
    var seenHandles = {};
    cards.forEach(function (card) {
      if (card.dataset.handle) seenHandles[card.dataset.handle] = true;
    });
    var loadMoreBtn = root.querySelector('[data-load-more]');
    var noResultsEl = root.querySelector('[data-no-results]');
    var statusEl = root.querySelector('[data-status]');
    var searchToggle = root.querySelector('[data-search-toggle]');
    var searchBar = root.querySelector('[data-search-bar]');
    var searchInput = root.querySelector('[data-search-input]');
    var searchClear = root.querySelector('[data-search-clear]');

    var state = {
      activeFilter: 'all',
      visibleLimit: pageSize,
      searchQuery: ''
    };

    // Build a shuffled order for "All", generated fresh on every page
    // load. rank[originalIndex] = its position once shuffled. Re-run any
    // time the underlying card set changes size (i.e. once background
    // page-loading, below, finishes bringing in entries beyond 250).
    var shuffledForAll = null;
    function rebuildShuffle() {
      if (!randomizeAll || cards.length <= 1) {
        shuffledForAll = null;
        return;
      }
      var order = cards.map(function (_, i) { return i; });
      for (var i = order.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = order[i];
        order[i] = order[j];
        order[j] = tmp;
      }
      var rank = new Array(order.length);
      order.forEach(function (originalIndex, position) {
        rank[originalIndex] = position;
      });
      shuffledForAll = order.map(function (originalIndex) {
        return cards[originalIndex];
      });
      cards.forEach(function (card, i) {
        card.dataset.shuffleRank = rank[i];
      });
    }
    rebuildShuffle();

    function cardTags(card) {
      return (card.dataset.tags || '')
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);
    }

    function getMatches() {
      var isAll = state.activeFilter === 'all';
      var source = isAll && shuffledForAll ? shuffledForAll : cards;

      var byFilter = isAll
        ? source
        : source.filter(function (card) {
          return cardTags(card).indexOf(state.activeFilter) !== -1;
        });

      if (!state.searchQuery) return byFilter;

      return byFilter.filter(function (card) {
        // data-search covers every field the entry has (community name,
        // campaign title, campaign type, theme tags) — falls back to
        // data-title alone if data-search is ever missing.
        var haystack = (card.dataset.search || card.dataset.title || '').toLowerCase();
        return haystack.indexOf(state.searchQuery) !== -1;
      });
    }

    function render() {
      var matches = getMatches();
      var usingShuffleOrder = state.activeFilter === 'all' && shuffledForAll;

      cards.forEach(function (card) {
        card.hidden = matches.indexOf(card) === -1;
        card.style.order = usingShuffleOrder ? card.dataset.shuffleRank : '';
      });

      matches.forEach(function (card, index) {
        card.classList.toggle('is-hidden-initial', index >= state.visibleLimit);
      });

      var shownCount = Math.min(state.visibleLimit, matches.length);

      if (loadMoreBtn) {
        loadMoreBtn.hidden = state.visibleLimit >= matches.length;
      }

      if (noResultsEl) {
        noResultsEl.hidden = matches.length !== 0;
      }

      if (statusEl) {
        statusEl.textContent = matches.length === 0
          ? 'No campaigns match this filter.'
          : 'Showing ' + shownCount + ' of ' + matches.length + ' campaigns.';
      }
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        if (pill.classList.contains('is-active')) return;

        pills.forEach(function (p) {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });
        pill.classList.add('is-active');
        pill.setAttribute('aria-pressed', 'true');

        state.activeFilter = pill.dataset.filter;
        state.visibleLimit = pageSize;
        render();
      });
    });

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        state.visibleLimit += pageSize;
        render();
      });
    }

    if (searchBar && searchInput) {
      if (searchToggle) {
        // A toggle button exists (pills are also shown): collapsed by
        // default, CSS animates the open/close via max-height + opacity
        // instead of an abrupt hidden-attribute toggle. `inert` keeps the
        // collapsed field out of the tab order and unclickable in
        // browsers that support it; harmless no-op otherwise.
        searchBar.inert = true;

        searchToggle.addEventListener('click', function () {
          var isOpen = searchBar.classList.contains('is-open');

          if (isOpen) {
            searchBar.classList.remove('is-open');
            searchBar.inert = true;
            searchToggle.setAttribute('aria-expanded', 'false');
            if (searchInput.value) {
              searchInput.value = '';
              state.searchQuery = '';
              state.visibleLimit = pageSize;
              render();
            }
          } else {
            searchBar.classList.add('is-open');
            searchBar.inert = false;
            searchToggle.setAttribute('aria-expanded', 'true');
            searchInput.focus();
          }
        });
      } else {
        // No toggle button (pills are off): the search bar has no reason
        // to hide behind a click, so it's rendered permanently open
        // (Liquid already added the "is-open" class server-side — this
        // just guarantees it stays interactive with or without JS).
        searchBar.classList.add('is-open');
        searchBar.inert = false;
      }

      // Wired regardless of whether a toggle button exists, so typing and
      // clearing always work once the search bar itself is present.
      searchInput.addEventListener('input', function () {
        state.searchQuery = searchInput.value.trim().toLowerCase();
        state.visibleLimit = pageSize;
        if (searchClear) searchClear.hidden = !searchInput.value;
        render();
      });

      if (searchClear) {
        searchClear.addEventListener('click', function () {
          searchInput.value = '';
          searchClear.hidden = true;
          state.searchQuery = '';
          state.visibleLimit = pageSize;
          searchInput.focus();
          render();
        });
      }
    }

    render();

    // Beyond 250 entries: pull in the rest of the Shopify pagination
    // pages in the background so filtering/search/"Load more" all see
    // the complete set, not just the first 250. See the file docblock.
    var totalPages = parseInt(root.dataset.totalPages, 10) || 1;
    var currentPage = parseInt(root.dataset.currentPage, 10) || 1;
    var sectionId = root.dataset.sectionId;

    if (totalPages > currentPage && sectionId) {
      fetchNextPage(currentPage + 1);
    }

    function fetchNextPage(page) {
      if (page > totalPages) {
        // All pages are in now — do the final shuffle and reveal.
        rebuildShuffle();
        render();
        return;
      }

      var base = window.location.pathname;
      var sep = base.indexOf('?') === -1 ? '?' : '&';
      var url = base + sep + 'section_id=' + encodeURIComponent(sectionId) + '&page=' + page;

      fetch(url)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fetchedRoot = doc.querySelector('[data-campaign-cards]');
          var fetchedPage = fetchedRoot ? parseInt(fetchedRoot.dataset.currentPage, 10) : NaN;

          // The whole point of this request was to get page N. Confirm the
          // server actually sent page N back (its own data-current-page
          // says so) before appending a single card from it. On this store,
          // ?page= hasn't reliably advanced the metaobjects paginate loop
          // through the Section Rendering API — the response comes back as
          // page 1 again — and appending that unconditionally is exactly
          // what was doubling every entry. If the page we got isn't the
          // page we asked for, stop the background load here rather than
          // add content that's really a repeat of what's already shown.
          if (fetchedPage !== page) {
            rebuildShuffle();
            render();
            return;
          }

          var fetchedCards = Array.prototype.slice.call(doc.querySelectorAll('[data-card]'));
          var grid = root.querySelector('[data-grid]');

          // Belt-and-suspenders: also skip any card whose handle is
          // already on the page, in case a future response passes the
          // page-number check above but still repeats an entry.
          var newCards = fetchedCards.filter(function (card) {
            var handle = card.dataset.handle;
            if (handle && seenHandles[handle]) return false;
            if (handle) seenHandles[handle] = true;
            return true;
          });

          newCards.forEach(function (card) {
            if (grid) grid.appendChild(card);
            cards.push(card);
          });

          // Reveal what's loaded so far (without reshuffling yet, to
          // avoid visibly reordering cards a visitor may already be
          // looking at); the final shuffle happens once every page is in.
          render();
          fetchNextPage(page + 1);
        })
        .catch(function () {
          // Network hiccup — stop quietly. Whatever loaded already stays
          // fully usable; a page refresh will retry from page 1.
        });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-campaign-cards]').forEach(initCampaignCards);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a merchant adds/edits the section in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-campaign-cards]');
    if (root) {
      delete root.dataset.campaignCardsInitialized;
      initCampaignCards(root);
    }
  });
})();
